import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { analysis as analysisApi } from '@/lib/api';

/* ============================================================
   Per-JD Print Report — printable / PDF export
   Renders EVERYTHING from ONE JD analysis (You vs JD, CV Audit,
   Interview Prep, Optimize LinkedIn) in a single long document
   optimised for printing to PDF.

   Opened in a new tab via `/dashboard/jd-report/:id?print=1`.
   When the `print=1` query param is present, the browser print
   dialog is triggered automatically once the JD data has loaded.
   ============================================================ */

export default function JDPrintReport() {
  const { id } = useParams();
  const location = useLocation();
  const shouldAutoPrint = new URLSearchParams(location.search).get('print') === '1';

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const d = await analysisApi.jdGet(id);
        if (!mounted) return;
        setData(d);
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || e?.message || 'Could not load this JD analysis.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  useEffect(() => {
    if (loading || !data || !shouldAutoPrint) return;
    const t = setTimeout(() => {
      try { window.print(); } catch (e) { /* no-op */ }
    }, 700);
    return () => clearTimeout(t);
  }, [loading, data, shouldAutoPrint]);

  if (loading) return <div className="sd-print-loading"><p>Loading your JD analysis…</p></div>;
  if (error) return <div className="sd-print-loading"><p>{error}</p></div>;
  if (!data) return <div className="sd-print-loading"><p>No JD analysis found.</p></div>;

  const r = data.result || {};
  const generatedAt = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const reqs = r.requirements || [];
  const audit = r.cv_audit || {};
  const rewrites = Array.isArray(audit.bullet_rewrites) ? audit.bullet_rewrites : [];
  const atsPresent = Array.isArray(audit.ats_keywords?.present) ? audit.ats_keywords.present : [];
  const atsMissing = Array.isArray(audit.ats_keywords?.missing) ? audit.ats_keywords.missing : [];
  const quantified = Array.isArray(audit.quantified_impact) ? audit.quantified_impact : [];
  const interview = r.interview || {};
  const l = r.linkedin || {};

  return (
    <article className="sd-print-report" data-testid="jd-print-report">
      {/* Cover */}
      <header className="sd-print-cover">
        <div className="sd-print-brand">SD·Pivot · JD Analyzer</div>
        <div className="sd-print-cover-eyebrow">Per-role application intelligence</div>
        <h1 className="sd-print-cover-title">{r.jd_title || 'Job description'}</h1>
        {r.jd_company && <p className="sd-print-cover-tagline">at {r.jd_company}</p>}
        <div className="sd-print-cover-stats">
          <div className="sd-print-cover-stat">
            <div className="lbl">Match score</div>
            <div className="val">{r.match_score ?? '—'}%</div>
          </div>
          <div className="sd-print-cover-stat">
            <div className="lbl">Requirements matched</div>
            <div className="val">
              {reqs.filter(x => x.status === 'strong').length}/{reqs.length}
            </div>
          </div>
        </div>
        {r.competitive_edge && <p className="sd-print-cover-headline">{r.competitive_edge}</p>}
        <div className="sd-print-cover-meta">
          <span>Generated {generatedAt}</span>
        </div>
      </header>

      {/* You vs the JD — Requirements coverage */}
      {reqs.length > 0 && (
        <Section title="You vs the JD" subtitle="Line-by-line coverage of the JD requirements against your CV.">
          <div className="sd-print-jd-reqs">
            {['strong', 'partial', 'gap'].map((status) => {
              const items = reqs.filter(x => x.status === status);
              if (!items.length) return null;
              const label = status === 'strong' ? 'Strongly matched' : status === 'partial' ? 'Partially covered' : 'Not covered';
              return (
                <div key={status} className="sd-print-jd-reqgroup">
                  <h3 className="sd-print-jd-reqgroup-head">{label} · {items.length}</h3>
                  <ul>
                    {items.map((x, i) => (
                      <li key={i}>
                        <div className="req-title">{x.req}</div>
                        {x.evidence && <div className="req-evidence"><strong>Evidence:</strong> {x.evidence}</div>}
                        {x.gap_detail && <div className="req-gap"><strong>Gap:</strong> {x.gap_detail}</div>}
                        {x.action && <div className="req-action"><strong>How to close:</strong> {x.action}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* CV Audit */}
      {(rewrites.length > 0 || atsMissing.length > 0 || quantified.length > 0) && (
        <Section title="CV Audit" subtitle="Line-by-line rewrites, ATS keyword coverage, and where to add numbers.">
          {rewrites.length > 0 && (
            <div className="sd-print-cvaudit-group">
              <h3 className="sd-print-cvaudit-head">Bullet rewrites</h3>
              {rewrites.map((b, i) => (
                <div key={i} className="sd-print-cvaudit-rewrite">
                  <div className="orig"><strong>Original:</strong> {b.original}</div>
                  <div className="new"><strong>Rewrite:</strong> {b.rewritten}</div>
                  {b.why && <div className="why"><em>Why:</em> {b.why}</div>}
                </div>
              ))}
            </div>
          )}
          {(atsPresent.length > 0 || atsMissing.length > 0) && (
            <div className="sd-print-cvaudit-group">
              <h3 className="sd-print-cvaudit-head">ATS keyword coverage</h3>
              {atsPresent.length > 0 && (
                <div className="sd-print-ats-block">
                  <div className="lbl">Present in your CV ({atsPresent.length})</div>
                  <div className="chips">
                    {atsPresent.map((k, i) => <span key={i} className="chip chip-good">{k.term}</span>)}
                  </div>
                </div>
              )}
              {atsMissing.length > 0 && (
                <div className="sd-print-ats-block">
                  <div className="lbl">Missing — add naturally ({atsMissing.length})</div>
                  <ul>
                    {atsMissing.map((k, i) => (
                      <li key={i}>
                        <span className="term">{k.term}</span>
                        <span className={`priority priority-${k.priority}`}>{k.priority}</span>
                        {k.suggestion && <div className="sug">{k.suggestion}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {quantified.length > 0 && (
            <div className="sd-print-cvaudit-group">
              <h3 className="sd-print-cvaudit-head">Add numbers where they're missing</h3>
              {quantified.map((q, i) => (
                <div key={i} className="sd-print-cvaudit-quant">
                  <div className="orig">{q.bullet}</div>
                  <div className="metric"><strong>Add:</strong> {q.suggested_metric}</div>
                  {q.why && <div className="why"><em>Why:</em> {q.why}</div>}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Interview Prep */}
      {(
        (interview.strategic?.length || 0) +
        (interview.technical?.length || 0) +
        (interview.behavioral?.length || 0) +
        (interview.ask_them?.length || 0)
      ) > 0 && (
        <Section title="Interview Prep" subtitle="Question banks with frameworks and drafted answers.">
          {['strategic', 'technical', 'behavioral'].map((track) => {
            const items = interview[track] || [];
            if (!items.length) return null;
            const label = track.charAt(0).toUpperCase() + track.slice(1);
            return (
              <div key={track} className="sd-print-interview-group">
                <h3 className="sd-print-interview-head">{label} · {items.length}</h3>
                {items.map((q, i) => (
                  <div key={i} className="sd-print-interview-q">
                    <div className="q-title">{q.q}</div>
                    {q.context && <div className="q-ctx">{q.context}</div>}
                    {q.framework && <div className="q-framework"><strong>Framework:</strong> {q.framework}</div>}
                    {q.bridge && <div className="q-bridge"><strong>Bridge:</strong> {q.bridge}</div>}
                    {q.draft_answer && (
                      <div className="q-draft">
                        <div className="lbl">Draft answer</div>
                        <p>{q.draft_answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
          {(interview.ask_them || []).length > 0 && (
            <div className="sd-print-interview-group">
              <h3 className="sd-print-interview-head">Ask them · {interview.ask_them.length}</h3>
              {interview.ask_them.map((q, i) => (
                <div key={i} className="sd-print-interview-q">
                  <div className="q-title">{q.q}</div>
                  {q.why && <div className="q-ctx"><em>Why ask:</em> {q.why}</div>}
                  {q.draft_answer && <p className="q-draft-inline">{q.draft_answer}</p>}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Optimize LinkedIn */}
      {(l.headline?.suggested || l.about?.suggested || (l.bullets || []).length > 0) && (
        <Section title="Optimize LinkedIn" subtitle="Headline, About and bullets rewritten for this role.">
          {l.headline?.suggested && (
            <div className="sd-print-li-block">
              <h3>Headline</h3>
              {l.headline.current && <div className="before"><em>Current:</em> {l.headline.current}</div>}
              <div className="after">{l.headline.suggested}</div>
              {l.headline.why && <div className="why"><em>Why:</em> {l.headline.why}</div>}
            </div>
          )}
          {l.about?.suggested && (
            <div className="sd-print-li-block">
              <h3>About section</h3>
              {l.about.current && <div className="before"><em>Current:</em> {l.about.current}</div>}
              <div className="after" style={{ whiteSpace: 'pre-line' }}>{l.about.suggested}</div>
              {l.about.why && <div className="why"><em>Why:</em> {l.about.why}</div>}
            </div>
          )}
          {(l.bullets || []).filter(b => (b.after || '').trim()).length > 0 && (
            <div className="sd-print-li-block">
              <h3>Experience bullets</h3>
              {l.bullets.filter(b => (b.after || '').trim()).map((b, i) => (
                <div key={i} className="bullet-pair">
                  {b.before && <div className="before"><em>Before:</em> {b.before}</div>}
                  <div className="after">{b.after}</div>
                </div>
              ))}
            </div>
          )}
          {(l.keywords || []).length > 0 && (
            <div className="sd-print-li-block">
              <h3>Keywords</h3>
              <div className="chips">
                {l.keywords.map((k, i) => {
                  const term = typeof k === 'string' ? k : (k?.term || '');
                  const vol = typeof k === 'string' ? 'niche' : (k?.volume || 'niche');
                  return <span key={i} className={`chip ${vol === 'high' ? 'chip-high' : 'chip-niche'}`}>{term} · {vol}</span>;
                })}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Saveable actions */}
      {(r.saveable_actions || []).length > 0 && (
        <Section title="Actions to save into your plan" subtitle="Specific moves to close the gap.">
          <ul className="sd-print-actions">
            {r.saveable_actions.map((a, i) => (
              <li key={i}>
                <strong>{a.title}</strong>
                <span> · {a.time}</span>
                {a.critical && <span className="crit"> · Critical</span>}
                {a.cost && <span> · {a.cost}</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Footer */}
      <footer className="sd-print-footer">
        <div className="brand">SD·Pivot · JD Analyzer report</div>
        <div className="date">Generated {generatedAt}</div>
      </footer>
    </article>
  );
}

/* ---------- helpers ---------- */

function Section({ title, subtitle, children }) {
  return (
    <section className="sd-print-section">
      <div className="sd-print-section-head">
        <h2 className="sd-print-section-title">{title}</h2>
        {subtitle && <p className="sd-print-section-sub">{subtitle}</p>}
      </div>
      <div className="sd-print-section-body">{children}</div>
    </section>
  );
}
